import { getSessionUUID } from '../components/lib/AuthToken';
import logger from "../utils/logger";
import { executeWithProgressTracking } from './progressTracking';


/**
 * Enhanced Warehouse API functions
 * For the new single-pallet-per-lane system
 */

// Create a new pallet
export const createPallet = async (palletCode, palletType = 'mixed_sku') => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                location: palletCode,
                pallet_type: palletType
            })
        };

        const response = await fetch('/enhanced_warehouse/create_pallet', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallet created successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error creating pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Create a new product
export const createProduct = async (productData) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(productData)
        };

        const response = await fetch('/enhanced_warehouse/create_product', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Product created successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error creating product:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Add product to pallet
export const addProductToPallet = async (palletId, productId, quantity, notes = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                pallet_id: palletId,
                product_id: productId,
                quantity: quantity,
                notes: notes
            })
        };

        const response = await fetch('/enhanced_warehouse/add_product_to_pallet', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Product added to pallet successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error adding product to pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Get all lanes with pallets
export const fetchEnhancedLanes = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const url = customerId 
            ? `/enhanced_warehouse/lanes?customer_id=${customerId}`
            : '/enhanced_warehouse/lanes';

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const response = await fetch(url, requestOptions);
        const responseBody = await response.json();

        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error fetching enhanced lanes:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Get specific lane pallet
export const fetchLanePallet = async (laneId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const response = await fetch(`/enhanced_warehouse/lane/${laneId}/pallet`, requestOptions);
        const responseBody = await response.json();

        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error("Error fetching lane pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
        };
    }
};

// Get pallet contents
export const fetchPalletContents = async (palletId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}/contents`, requestOptions);
        const responseBody = await response.json();

        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error("Error fetching pallet contents:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
        };
    }
};

// Get all products
export const fetchAllProducts = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const url = customerId 
            ? `/enhanced_warehouse/products?customer_id=${customerId}`
            : '/enhanced_warehouse/products';

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const response = await fetch(url, requestOptions);
        const responseBody = await response.json();

        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error fetching products:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Setup new lane
export const setupLane = async (laneId, customerId, palletType = 'mixed_sku') => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                lane_id: laneId,
                customer_id: customerId,
                pallet_type: palletType
            })
        };

        const response = await fetch('/enhanced_warehouse/setup_lane', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Lane setup successfully',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error("Error setting up lane:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
        };
    }
};

// Generate QR code for enhanced warehouse items
export const generateEnhancedQRCode = async (itemId, itemType, qrData) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ 
                id: itemId,
                table: itemType, // 'pallet' or 'product'
                qr_data: qrData
            })
        };

        const response = await fetch('/enhanced_warehouse/generate-qr', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'QR code generated successfully',
            status: response.status,
            data: responseBody || null,
            qr_url: responseBody.qr_url || null
        };
    } catch (err) {
        logger.error("Error generating enhanced QR code:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
        };
    }
};

// Update product
export const updateProduct = async (productId, productData) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(productData)
        };

        const response = await fetch(`/enhanced_warehouse/product/${productId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Product updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error updating product:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Update pallet
export const updatePallet = async (palletId, palletData) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(palletData)
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallet updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error updating pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Update pallet item quantity
export const updatePalletItemQuantity = async (palletId, itemId, quantity, notes = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                quantity: quantity,
                notes: notes
            })
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}/item/${itemId}/update_quantity`, requestOptions);
        
        // Check if response is actually JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const textResponse = await response.text();
            logger.error('Non-JSON response received:', textResponse);
            throw new Error(`Server returned ${response.status}: ${textResponse.substring(0, 200)}`);
        }
        
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Item quantity updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error updating item quantity:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Remove item from pallet
export const removePalletItem = async (palletId, itemId, productId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                product_id: productId
            })
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}/item/${itemId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Item removed successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error removing item:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Move item between pallets
export const movePalletItem = async (sourcePalletId, itemId, destinationPalletId, productId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                destination_pallet_id: destinationPalletId,
                product_id: productId
            })
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${sourcePalletId}/item/${itemId}/move`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Item moved successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error moving item:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Move pallet to different location (lane)
export const movePalletLocation = async (palletId, destinationPalletId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                destination_pallet_id: destinationPalletId
            })
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}/move_location`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallet moved successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error moving pallet location:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

export const bulkMoveStandardPallets = async (sourcePalletIds, destinationLane) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch('/enhanced_warehouse/pallets/bulk_move', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                source_pallet_ids: sourcePalletIds,
                destination_lane: destinationLane
            })
        });
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallets moved successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error('Error bulk moving pallets:', err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

export const fetchStandardPalletLaneCapacity = async () => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch('/enhanced_warehouse/pallets/standard_lane_capacity', {
            headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        const responseBody = await response.json();
        return {
            message: responseBody.message || responseBody.error || 'Lane capacity loaded',
            status: response.status,
            data: responseBody.lanes || [],
            success: response.ok && responseBody.success,
        };
    } catch (err) {
        logger.error('Error loading standard pallet lane capacity:', err);
        return { message: err.message || 'An error occurred', status: 500, data: [], success: false };
    }
};

// Move product between pallets using product_id, old_pallet_id, new_pallet_id
export const moveProductBetweenPallets = async (productId, oldPalletId, newPalletId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                product_id: productId,
                old_pallet_id: oldPalletId,
                new_pallet_id: newPalletId
            })
        };

        const response = await fetch(`/api/warehouse/move-product`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Product moved successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error moving product between pallets:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Delete pallet
export const deletePallet = async (palletId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/pallet/${palletId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallet deleted successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error deleting pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Delete product
export const deleteProduct = async (productId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/product/${productId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Product deleted successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error deleting product:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Download warehouse lanes as Excel
export const downloadWarehouseLanes = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();
        
        let url = '/enhanced_warehouse/download/warehouse-lanes';
        if (customerId) {
            url += `?customer_id=${customerId}`;
        }

        const requestOptions = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(url, requestOptions);
        
        if (response.ok) {
            const blob = await response.blob();
            const filename = response.headers.get('content-disposition')
                ?.split('filename=')[1]
                ?.replace(/"/g, '') || 'warehouse_lanes.xlsx';
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            return {
                success: true,
                message: 'Warehouse lanes downloaded successfully',
                status: 200
            };
        } else {
            const errorData = await response.json();
            return {
                success: false,
                message: errorData.error || 'Failed to download warehouse lanes',
                status: response.status
            };
        }
    } catch (err) {
        logger.error("Error downloading warehouse lanes:", err);
        return {
            success: false,
            message: err.message || 'An error occurred',
            status: 500
        };
    }
};

// Download products as Excel
export const downloadProducts = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();
        
        let url = '/enhanced_warehouse/download/products';
        if (customerId) {
            url += `?customer_id=${customerId}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        });

        if (response.ok) {
            // Get filename from response headers
            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'products.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create blob and download
            const blob = await response.blob();
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            return {
                message: 'Products downloaded successfully',
                success: true,
                status: 200
            };
        } else {
            const errorData = await response.json();
            return {
                success: false,
                message: errorData.error || 'Failed to download products',
                status: response.status
            };
        }
    } catch (err) {
        logger.error("Error downloading products:", err);
        return {
            success: false,
            message: err.message || 'An error occurred',
            status: 500
        };
    }
};

// Add product to pallet
export const inboundProducts = async (inboundData, user_id) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                inbound_data: inboundData,
                user_id: user_id
            })
        };

        const response = await fetch('/enhanced_warehouse/inbound_products', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Products added successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error adding product to pallet:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Bulk edit pallets
export const bulkEditPallets = async (palletIds, updates, userId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                pallet_ids: palletIds,
                updates: updates,
                user_id: userId
            })
        };

        const response = await fetch('/enhanced_warehouse/pallets/bulk_edit', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallets updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error bulk editing pallets:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Bulk edit pallets
export const bulkDeletePallets = async (palletIds) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                pallet_ids: palletIds,
            })
        };

        const response = await fetch('/enhanced_warehouse/pallets/bulk_delete', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Pallets deleted successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error bulk deleting pallets:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Fetch inbound warehouse reports
export const fetchInboundReports = async (startDate, endDate, customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                startDate,
                endDate,
                customerId // Optional customer filter for warehouse page
            })
        };

        const response = await fetch('/enhanced_warehouse/reports/inbound', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Inbound reports fetched successfully',
            status: response.status,
            data: responseBody.data || [],
            success: response.ok && responseBody.success,
        };
    } catch (err) {
        logger.error("Error fetching inbound reports:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

// Fetch dispatch warehouse reports
export const fetchDispatchReports = async (startDate, endDate, customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                startDate,
                endDate,
                customerId // Optional customer filter for warehouse page
            })
        };

        const response = await fetch('/enhanced_warehouse/reports/dispatch', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Dispatch reports fetched successfully',
            status: response.status,
            data: responseBody.data || [],
            success: response.ok && responseBody.success,
        };
    } catch (err) {
        logger.error("Error fetching dispatch reports:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false,
        };
    }
};

//-------------------------------------------------------------------------------------------------

export const regenerateQRCode = async (itemId, itemType) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ 
                id: itemId,
                table: itemType // 'products' or 'pallets' (plural for table names)
            })
        };

        const response = await fetch('/enhanced_warehouse/regenerate-qr', requestOptions);
        const responseBody = await response.json();

        const result = {
            message: responseBody.message || responseBody.error || 'QR code regenerated successfully',
            status: response.status,
            data: responseBody || null,
            qr_url: responseBody.qr_url || null,
            success: responseBody.success !== undefined ? responseBody.success : response.ok
        };
        
        return result;
    } catch (err) {
        logger.error("Error regenerating QR code:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Bulk regenerate QR codes for all items in a table with real-time progress using file-based polling
export const bulkRegenerateQRCodes = async (table, progressCallback) => {
    return new Promise(async (resolve, reject) => {
        try {
            const sessionToken = await getSessionUUID();
            
            // Step 1: Start the bulk regeneration process and get a UUID
            const startResponse = await fetch('/enhanced_warehouse/bulk-regenerate-qr-start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ table: table })
            });

            if (!startResponse.ok) {
                const errorData = await startResponse.json();
                throw new Error(errorData.error || `HTTP error! status: ${startResponse.status}`);
            }

            const startData = await startResponse.json();
            const progressUuid = startData.uuid;
            
            // Step 2: Poll for progress updates every 5 seconds with retry logic
            let retryCount = 0;
            const maxRetries = 3;
            
            const pollInterval = setInterval(async () => {
                try {
                    const progressResponse = await fetch(`/enhanced_warehouse/bulk-regenerate-qr-progress/${progressUuid}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    });

                    if (!progressResponse.ok) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            clearInterval(pollInterval);
                            reject(new Error(`Failed to get progress after ${maxRetries} retries: ${progressResponse.status}`));
                        }
                        return; // Don't clear interval, just skip this poll and retry next time
                    }
                    
                    // Reset retry count on successful response
                    retryCount = 0;

                    const progressData = await progressResponse.json();

                    if (progressData.error) {
                        clearInterval(pollInterval);
                        reject(new Error(progressData.error));
                        return;
                    }

                    // Call progress callback with current progress
                    if (progressCallback && progressData.total > 0) {
                        progressCallback({
                            processed: progressData.processed || 0,
                            total: progressData.total
                        });
                    }

                    // Check if completed
                    if (progressData.completed) {
                        clearInterval(pollInterval);
                        resolve({
                            message: progressData.message || 'QR regeneration completed successfully',
                            status: 200,
                            success: true,
                            processed: progressData.processed,
                            total: progressData.total,
                            success_count: progressData.success_count || progressData.processed,
                            error_count: progressData.error_count || 0,
                            errors: progressData.errors || []
                        });
                        return;
                    }

                } catch (pollError) {
                    retryCount++;
                    if (retryCount >= maxRetries) {
                        clearInterval(pollInterval);
                        reject(pollError);
                    }
                    // Don't clear interval on network errors, just retry
                }
            }, 5000); // Poll every 5 seconds

            // Add timeout after 30 minutes (longer timeout for large datasets)
            setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Process timed out after 30 minutes'));
            }, 1800000);

        } catch (err) {
            logger.error("Error in bulk QR regeneration:", err);
            reject({
                message: err.message || 'An error occurred',
                status: 500,
                success: false
            });
        }
    });
};
// Temporary cleanup function for missed pallets with missing QR ID fields
export const tempCleanupMissedPallets = async (onProgress = () => {}) => {
    try {
        const sessionToken = await getSessionUUID();
        
        if (!sessionToken) {
            throw new Error('No authentication token found');
        }

        // Step 1: Start the cleanup process
        const startResponse = await fetch('/enhanced_warehouse/temp-cleanup-missed-pallets', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        });

        if (!startResponse.ok) {
            throw new Error(`Failed to start cleanup: ${startResponse.status}`);
        }

        const startResult = await startResponse.json();
        const progressUuid = startResult.uuid;

        // Step 2: Poll for progress updates every 5 seconds with retry logic
        return new Promise((resolve, reject) => {
            let retryCount = 0;
            const maxRetries = 3;
            
            const pollInterval = setInterval(async () => {
                try {
                    const progressResponse = await fetch(`/enhanced_warehouse/bulk-regenerate-qr-progress/${progressUuid}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    });

                    if (!progressResponse.ok) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            clearInterval(pollInterval);
                            reject(new Error(`Failed to get progress after ${maxRetries} retries: ${progressResponse.status}`));
                        }
                        return; // Don't clear interval, just skip this poll and retry next time
                    }
                    
                    // Reset retry count on successful response
                    retryCount = 0;

                    const progressData = await progressResponse.json();
                    
                    // Call progress callback
                    onProgress({
                        processed: progressData.processed || 0,
                        total: progressData.total || 0,
                        message: progressData.message || 'Processing...'
                    });

                    // Check if completed
                    if (progressData.completed) {
                        clearInterval(pollInterval);
                        resolve({
                            success: true,
                            message: progressData.message,
                            processed: progressData.processed,
                            total: progressData.total,
                            success_count: progressData.success_count,
                            error_count: progressData.error_count,
                            errors: progressData.errors
                        });
                    }
                } catch (pollError) {
                    retryCount++;
                    if (retryCount >= maxRetries) {
                        clearInterval(pollInterval);
                        reject(pollError);
                    }
                    // Don't clear interval on network errors, just retry
                }
            }, 5000); // Poll every 5 seconds

            // Add timeout after 30 minutes
            setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Cleanup process timed out after 30 minutes'));
            }, 1800000);
        });
        
    } catch (error) {
        throw error;
    }
};

// New simplified bulk QR regeneration using reusable progress tracking
export const bulkRegenerateQRCodesNew = async (table, progressCallback) => {
    try {
        return await executeWithProgressTracking(
            '/enhanced_warehouse/bulk-regenerate-qr-start',
            { table: table },
            progressCallback
        );
    } catch (error) {
        throw error;
    }
};

// Bulk create pallets with progress tracking
export const bulkCreatePallets = async (palletData, progressCallback) => {
    try {
        return await executeWithProgressTracking(
            '/enhanced_warehouse/bulk-create-pallets-start',
            palletData,
            progressCallback
        );
    } catch (error) {
        throw error;
    }
};

// historical warehouse data
export const downloadHistoricalStockReport = async (stockDate, customerId = null) => {
    try {
            const sessionToken = await getSessionUUID();

        if (!customerId) {
            return {
                success: false,
                message: 'Please select a customer',
                status: 400
            };
        }

        if (!stockDate) {
            return {
                success: false,
                message: 'Please select a date',
                status: 400
            };
        }

        const params = new URLSearchParams({
            customer_id: customerId,
            date: stockDate
        });

        let url =
            `/enhanced_warehouse/download/historical-stock?${params.toString()}`;

        const requestOptions = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(url, requestOptions);
        
        if (response.ok) {
            const blob = await response.blob();
            const filename = response.headers.get('content-disposition')
                ?.split('filename=')[1]
                ?.replace(/"/g, '') || 'historical_stock_report.xlsx';
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            return {
                success: true,
                message: 'Historical stock report downloaded successfully',
                status: 200
            };
        } else {
            const errorData = await response.json();
            return {
                success: false,
                message: errorData.error || 'Failed to download historical stock report',
                status: response.status
            };
        }
    } catch (err) {
        logger.error("Error downloading historical stock report:", err);
        return {
            success: false,
            message: err.message || 'An error occurred',
            status: 500
        };
    }
};

export const fetchAllPallets = async () => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch('/enhanced_warehouse/pallets/rpc', requestOptions);
        const responseBody = await response.json();
        return {
            message: responseBody.message || responseBody.error || 'All pallets fetched successfully',
            status: response.status,
            data: responseBody.pallets || [],
            success: response.ok && responseBody.success,
        };
    } catch (err) {
        logger.error("Error fetching all pallets:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            data: [],
            success: false,
        };
    }
};

export const generate_pick_note = async (order_id) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ 
                order_id,
            })
        };

        const response = await fetch('/enhanced_warehouse/generate_pick_note', requestOptions);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate pick note');
        }

        // Handle PDF download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        
        // Extract filename from response headers or use default with order_id
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `PickNote-Order-${order_id}.pdf`; // Default fallback using order_id
        if (contentDisposition) {
            // Try multiple regex patterns to extract filename
            let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
            if (!filenameMatch) {
                filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/);
            }
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return {
            message: 'Pick note generated and downloaded successfully',
            status: response.status,
        };
    } catch (err) {
        logger.error("Error generating pick note:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
        };
    }
};